<template>
  <div>
    <v-dialog v-model="dialog" persistent max-width="720">
      <v-card>
        <v-card-title class="headline">{{ title }}</v-card-title>
        <v-card-text>
          <v-form v-model="valid" ref="iform" lazy-validation>
            <item-view v-model="editedItem" :vat-included="vatIncluded"></item-view>
          </v-form>
        </v-card-text>
        <v-card-actions class="pl-3 pr-3">
          <v-btn color="grey darken-1 white--text" @click.native="close">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="green white--text" @click.native="save(editedItem)" :disabled="!valid">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card>
      <v-card-text>
        <v-data-table :headers="itemHeaders" :items="items" hide-actions>
          <template slot="headers" slot-scope="props">
            <tr>
              <th v-for="header in props.headers" :key="header.text" :class="`text-xs-${header.align}`">
                {{ header.text }}
              </th>
              <th class="text-xs-right">
                <v-btn fab dark small color="primary" class="text-xs-center" @click="addItem" v-if="!isDisabled">
                  <v-icon dark>playlist_add</v-icon>
                </v-btn>
              </th>
            </tr>
          </template>
          <template slot="items" slot-scope="props">
            <td class="text-xs-center">{{ props.index + 1 }}</td>
            <td>{{ props.item.name }}</td>
            <td class="text-xs-right">{{ props.item.quantity }}</td>
            <td class="text-xs-right">{{ props.item.unitPrice | currency('', 2) }}</td>
            <td class="text-xs-right">
              {{ (props.item.quantity * props.item.unitPrice) | currency('', 2) }}
            </td>
            <td class="text-xs-right">
              {{ (props.item.quantity * props.item.unitPrice * vatPercentage / 100) | currency('', 2) }}
            </td>
            <td class="text-xs-right">
              {{ ((props.item.quantity * props.item.unitPrice) + (props.item.quantity * props.item.unitPrice * vatPercentage / 100)) | currency('', 2) }}
            </td>
            <td class="text-xs-right">
              <v-btn icon class="mx-0" @click="editItem(props.item)" :disabled="isDisabled">
                <v-icon small color="teal">edit</v-icon>
              </v-btn>
              <v-btn icon class="mx-0" @click="deleteItem(props.item)" :disabled="isDisabled">
                <v-icon small color="pink">delete</v-icon>
              </v-btn>
            </td>
          </template>
          <template slot="footer">
            <td colspan="3" class="grey lighten-3 text-xs-right"></td>
            <td class="grey lighten-3 text-xs-right">
              <strong>Total</strong>
            </td>
            <td class="grey lighten-3 text-xs-right">
              {{ sumAmounts | currency('',2) }}
            </td>
            <td class="grey lighten-3 text-xs-right">
              {{ sumTaxes | currency('',2) }}
            </td>
            <td class="grey lighten-3 text-xs-right">
              {{ sumTotals | currency('',2) }}
            </td>
            <td colspan="2" class="grey lighten-3 text-xs-right">
              Vat {{ vatPercentage }}%
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import swal from 'sweetalert2'
import ItemView from './Item'

export default {
  props: {
    value: Array,
    isDisabled: {
      type: Boolean,
      default: false
    },
    vatIncluded: {
      type: Boolean,
      default: true
    }
  },
  components: { ItemView },
  data() {
    return {
      dialog: false,
      valid: false,
      items: this.value,
      editedIndex: -1,
      editedItem: null,
      defaultItem: {
        quantity: 1,
        unitPrice: 0.0
      },
      itemHeaders: [
        { text: '#', sortable: false, align: 'center' },
        { text: 'Name', value: 'name', sortable: false, align: 'left' },
        { text: 'Quantity', value: 'quantity', sortable: false, align: 'right' },
        { text: 'Price', value: 'unitPrice', sortable: false, align: 'right' },
        { text: 'Amount', value: 'totalPrice', sortable: false, align: 'right' },
        { text: 'Vat', value: 'taxPrice', sortable: false, align: 'right' },
        { text: 'Total', value: 'total', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      vat: 'systemSetting/vat'
    }),
    vatPercentage () {
      return (this.vatIncluded) ? this.vat : 0.0
    },
    title () {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
    sumAmounts() {
      if(this.items)
        return this.items.reduce((accumulator, currentValue) => accumulator + (currentValue.quantity * currentValue.unitPrice), 0)
      else
        return 0.0
    },
    sumTaxes() {
      if(this.items)
        return this.items.reduce((accumulator, currentValue) => accumulator + (currentValue.quantity * currentValue.unitPrice * this.vatPercentage / 100), 0)
      else
        return 0.0
    },
    sumTotals() {
      return this.sumAmounts + this.sumTaxes
    },
  },
  watch: {
    value () {
      this.items = this.value
    }
  },
  methods: {
    reset() {
      this.editedIndex = -1
      this.editedItem = Object.assign({}, this.defaultItem)
    },
    close () {
      this.dialog = false
      setTimeout(() => {
        this.reset()
      }, 300)
    },
    addItem (){
      this.reset()
      this.dialog = true
    },
    editItem (item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
    deleteItem(item) {
      swal({
        title: 'Delete quotation',
        text: 'Please, confirm your action.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Confirm'
      }).then(result => {
        if (result.value === true) {
          this.items.splice(this.items.indexOf(item), 1)
        }
      })
    },
    save(item) {
      if (this.$refs.iform.validate()) {
        if(this.editedIndex < 0){
          this.items.push(item)
        }else{
          this.items.splice(this.editedIndex, 1, item)
        }
        this.close()
      }
    }
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/item/ItemList.vue